import React from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/react-hooks';

import { Stage, TeaserContainer, Teaser, Container } from '@ccg/fischerappelt';

import relatedCasesQuery from '../../../graphql/queries/relatedCases.graphql';

import Embeds from '../../shared/Embeds/Embeds';
import PageTemplate from '../templates/page';
import { removeSpecialCharacters } from '../../../helper';

import { withTranslation } from '../../../i18n';

const Case = ({ data, site, t }) => {
  if (!data.entry) return null;

  const { cookieLayer, entry, footer, teaserSettings, seoDefaults, navigation, social } = data;
  const { casesOverviewPage } = navigation;
  const { relatedCasesCount } = teaserSettings;

  const {
    image,
    video,
    loop,
    tags,
    solutions,
    industries,
    tasks,
    client,
    title,
    subline,
    excerpt,
    embedsCopy,
    embeds
  } = entry;

  const limit = relatedCasesCount || 4;

  const { data: relatedData } = useQuery(relatedCasesQuery, {
    variables: {
      site,
      currentId: entry.id,
      limit,
      relatedTo: [...solutions, ...industries, ...tasks, ...tags].map(item => parseFloat(item.id))
    }
  });

  const relatedEntries = relatedData && relatedData.relatedEntries;

  return (
    <PageTemplate
      navigation={navigation}
      seo={entry}
      footer={footer}
      social={social}
      seoDefaults={seoDefaults}
      cookieLayer={cookieLayer}
      hasAccentLine
    >
      <Stage
        size={entry.contentType === 'advertising' ? 'large' : 'small'}
        image={
          image &&
          image.length > 0 && {
            ...image[0],
            srcset: {
              '1440w': image[0].width1440,
              '960w': image[0].width960,
              '720w': image[0].width720,
              '375w': image[0].width375
            }
          }
        }
        video={video && video.length > 0 && video[0]}
        loop={loop}
        title={removeSpecialCharacters(title)}
        headline={title}
        subline={subline}
        tags={tags}
        newsFormat={client[0].title}
        excerpt={excerpt}
        breadcrumbLink={
          casesOverviewPage && casesOverviewPage[0]
            ? {
                label: removeSpecialCharacters(casesOverviewPage[0].title),
                href: {
                  pathname: '/standard',
                  query: { uri: casesOverviewPage[0].uri }
                },
                asPath: `/${casesOverviewPage[0].uri}`
              }
            : undefined
        }
        hasAccentLine
      />

      <Embeds embedsCopy={embedsCopy} embeds={embeds} />

      {relatedEntries && relatedEntries.length > 0 && (
        <Container variant="default" headline={t('teaser.relatedCases')}>
          <TeaserContainer>
            {relatedEntries.map((item, index) => (
              <Teaser
                key={item.id}
                index={index}
                topic={item.client && item.client.length > 0 && item.client[0].title}
                accentColor={
                  item.client &&
                  item.client.length > 0 && {
                    primary: item.client[0].ciColorPrimary,
                    secondary: item.client[0].ciColorSecondary
                  }
                }
                image={
                  item.image.length > 0 && {
                    ...item.image[0],
                    srcset: {
                      '1440w': item.image[0].width1440,
                      '900w': item.image[0].width900,
                      '720w': item.image[0].width720,
                      '390w': item.image[0].width390
                    }
                  }
                }
                video={item.video.length > 0 && item.video[0]}
                headline={item.title}
                link={{
                  href: { pathname: '/case', query: { slug: item.slug } },
                  asPath: `/${item.uri}`
                }}
                tags={item.tags}
                cta={t('teaser.read')}
              />
            ))}
          </TeaserContainer>
        </Container>
      )}
    </PageTemplate>
  );
};

Case.propTypes = {
  site: PropTypes.string.isRequired,
  data: PropTypes.shape().isRequired,
  t: PropTypes.func.isRequired
};

Case.defaultProps = {};

export default withTranslation('common')(Case);
